import { useEffect, useState } from 'react';
import { weatherGreetingApi } from '../../lib/api';
import {
  resolveWeatherGreetingHeadingDisplay,
  type WeatherGreetingHeadingDisplay,
} from '../../lib/resolveWeatherGreetingHeadingDisplay';
import {
  GreetingEarlyMorningIcon,
  GreetingLateMorningIcon,
} from './GreetingTimeOfDayIcon';
import {
  AfternoonIcon,
  EveningIcon,
  LateNightIcon,
} from '../task/employer/EmployerTimeBucketIcon';

type WeatherGreetingHeadingEmojiProps = {
  hour: number;
};

const WeatherGreetingHeadingSvg = ({
  display,
}: {
  display: Extract<WeatherGreetingHeadingDisplay, { kind: 'svg' }>;
}) => {
  const iconClassName = 'inline-flex h-8 w-8 shrink-0 items-center justify-center';

  if (display.variant === 'earlyMorning') {
    return (
      <span className={iconClassName} aria-hidden>
        <GreetingEarlyMorningIcon />
      </span>
    );
  }

  if (display.variant === 'lateMorning') {
    return (
      <span className={iconClassName} aria-hidden>
        <GreetingLateMorningIcon />
      </span>
    );
  }

  if (display.variant === 'afternoon') {
    return (
      <span className={iconClassName} aria-hidden>
        <AfternoonIcon />
      </span>
    );
  }

  if (display.variant === 'evening') {
    return (
      <span className={iconClassName} aria-hidden>
        <EveningIcon />
      </span>
    );
  }

  return (
    <span className={iconClassName} aria-hidden>
      <LateNightIcon />
    </span>
  );
};

export const WeatherGreetingHeadingEmoji = ({ hour }: WeatherGreetingHeadingEmojiProps) => {
  const [emoji, setEmoji] = useState<string | null>(null);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const response = await weatherGreetingApi.current();
        if (!response.success || !response.data?.visible || !response.data.weatherEmoji) {
          return;
        }
        setEmoji(response.data.weatherEmoji);
      } catch {
        // Silent failure — heading emoji is optional
      }
    };
    void load();
  }, []);

  if (!emoji) {
    return null;
  }

  const display = resolveWeatherGreetingHeadingDisplay(emoji, hour);

  if (display.kind === 'svg') {
    return <WeatherGreetingHeadingSvg display={display} />;
  }

  return (
    <span className="inline-flex h-8 w-8 items-center justify-center text-2xl leading-none" aria-hidden>
      {display.emoji}
    </span>
  );
};
